import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import ConfirmationDialog from 'components/global/ConfirmationDialog';
import Image from 'components/global/Image';
import LottiePlayer from 'components/global/LottiePlayer';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalTitle } from 'components/global/styled/Modal.styled';

import useDebounce from 'hooks/useDebounce';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';

import AppLogger from 'utils/AppLogger';
import PATHS from 'utils/paths';

import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { faCheck, faTimes } from '@fortawesome/pro-solid-svg-icons';

import { useTranslateProductName } from './OTOStore';
import * as S from './OTOStoreCartModal.styled';
import { updateUserCart } from './OTOStoreSlice';

const OTOStoreCartModal = NiceModal.create(({ modalName }) => {
	const navigate = useNavigate();
	const { Currency } = useLocale();
	const modal = useModal();
	const { closeModal } = useModalRouter();
	const userCart = useSelector((state) => state.otoStore.cart);
	const { translateProductName } = useTranslateProductName();

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const handleCheckoutOrder = () => {
		navigate(PATHS.OTO_STORE_CHECKOUT);
		handleCloseModal();
	};

	return (
		<Modal position="right" fullwidth maxWidth="x-small">
			<ModalTitle>
				<Typography variant="p2" fontWeight="500">
					yourCart
				</Typography>
			</ModalTitle>
			<S.Content>
				{userCart?.items?.map((product) => (
					<CartProduct
						key={product?.productId}
						productId={product?.productId}
						name={translateProductName(product)}
						image={product?.image}
						attribute={product?.attribute}
						price={product?.itemSubTotal}
						quantity={product?.quantity}
						currency={Currency}
					/>
				))}
				{!userCart?.isLoading && userCart?.items?.length === 0 && (
					<S.NoCartContainer>
						<Image
							url="https://storage.googleapis.com/tryoto-public/public/assets/icons/OTO-Store/empty-cart.svg"
							width="11rem"
							height="15rem"
							className="empty-cart-img"
						/>
						<S.NoCartContent>
							<Typography variant="p2" fontWeight="700">
								noProductInCart
							</Typography>
							<Typography color="var(--content-3)">noProductInCartDescription</Typography>
						</S.NoCartContent>
					</S.NoCartContainer>
				)}
			</S.Content>
			{userCart?.items?.length > 0 && (
				<S.CartSummaryContainer>
					<S.Divider />
					<S.SummaryRow>
						<Typography color="var(--content-3)">subTotal</Typography>
						<Typography color="var(--content-3)" fontWeight="500" withCurrency>
							{userCart?.subTotal}
						</Typography>
					</S.SummaryRow>
					<S.SummaryRow>
						<Typography color="var(--content-3)">shippingFee</Typography>
						<Typography color="var(--content-4)" fontWeight="500" withCurrency>
							{userCart?.shippingTotal}
						</Typography>
					</S.SummaryRow>
					{userCart?.discount && (
						<S.SummaryRow>
							<Typography color="var(--content-3)">discount</Typography>
							<Typography color="var(--content-4)" fontWeight="500" withCurrency>
								{userCart?.discount}
							</Typography>
						</S.SummaryRow>
					)}
					<S.Divider />
					<S.SummaryRow>
						<Typography variant="p2" fontWeight="500">
							grandTotal
						</Typography>
						<Typography variant="p1" fontWeight="500" withCurrency>
							{userCart?.grandTotal}
						</Typography>
					</S.SummaryRow>
				</S.CartSummaryContainer>
			)}

			<ModalActions>
				<Button label="cancel" variant="action" onClick={handleCloseModal} />
				<Button
					label="checkoutNow"
					variant="blue"
					onClick={handleCheckoutOrder}
					loading={userCart.isLoading}
					disabled={userCart?.items?.length === 0}
					size="large"
				/>
			</ModalActions>
		</Modal>
	);
});

const CartProduct = ({ productId, name, image, price, currency, quantity }) => {
	const inputRef = useRef(null);
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(false);

	const [productQuantity, setProductQuantity] = useState(quantity);

	const debouncedValue = useDebounce(productQuantity, 1000);

	const updateQuantity = useCallback(
		async (newValue) => {
			try {
				setIsLoading(true);
				if (Number.isNaN(newValue)) {
					setProductQuantity(quantity);
					return;
				}
				if (+quantity === +newValue) {
					return;
				}

				await dispatch(
					updateUserCart({
						productId,
						quantity: newValue
					})
				).unwrap();
				setProductQuantity(newValue);
			} catch (error) {
				AppLogger.error(error);
			} finally {
				setIsLoading(false);
			}
		},
		[quantity]
	);

	useEffect(() => {
		if (debouncedValue !== quantity && document.activeElement !== inputRef.current) {
			updateQuantity(debouncedValue);
		}
	}, [debouncedValue]);

	const removeProduct = async (productId) => {
		try {
			setIsLoading(true);
			await dispatch(
				updateUserCart({
					productId,
					quantity: 0
				})
			).unwrap();
		} catch (error) {
			AppLogger.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<S.ProductContainer>
			{isLoading && (
				<S.LoadingContainer>
					<LottiePlayer name="loading" className="loading" />
				</S.LoadingContainer>
			)}
			<Image
				url={image}
				width="9rem"
				height="9rem"
				style={{
					borderRadius: '0.5rem',
					minWidth: '9rem'
				}}
			/>
			<S.ProductActionContainer isLoading={isLoading}>
				<S.Row>
					<Typography maxlines={1} noWrap>
						{name}
					</Typography>
					<Typography variant="p2" withCurrency currency={currency} css={{ overflow: 'unset' }}>
						{price}
					</Typography>
				</S.Row>
				<S.Row>
					<S.ProductQuantityContainer>
						<IntegerInput
							ref={inputRef}
							showArrows
							min={1}
							value={productQuantity}
							setValue={(e) => setProductQuantity(e < 1 ? 1 : e)}
							onBlur={(e) => updateQuantity(+e.target.value)}
						/>
					</S.ProductQuantityContainer>
					<DeleteButton productId={productId} onRemoveProduct={removeProduct} />
				</S.Row>
			</S.ProductActionContainer>
		</S.ProductContainer>
	);
};

const DeleteButton = ({ productId, onRemoveProduct }) => {
	const [deleteButtonRef, setDeleteButtonRef] = useState(null);

	const handleDeleteButtonClick = async () => {
		await onRemoveProduct(productId);
	};

	return (
		<>
			<Button
				ref={setDeleteButtonRef}
				icon={faTrashCan}
				color="var(--red)"
				variant="transparent"
				css={{
					borderColor: 'var(--red) !important'
				}}
			/>

			<ConfirmationDialog
				buttonRef={deleteButtonRef}
				placement="bottom-end"
				title="otoStoreRemoveProduct"
				subTitle="otoStoreRemoveProductDescription"
				icon={faTrashCan}
				leftIcon={faTimes}
				rightLabel="yes"
				leftLabel="cancel"
				rightIcon={faCheck}
				rightAction={handleDeleteButtonClick}
			/>
		</>
	);
};

export default OTOStoreCartModal;
